import { ErrorHandler, Injectable, NgZone, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { NotificationService } from '../../services/notification.service';
import { environment } from '../../environment/environment';

const sentryHandler: ErrorHandler | null = environment.sentryDsn
  ? Sentry.createErrorHandler({ showDialog: false })
  : null;

interface ApiError {
  message?: string;
  errors?: string[];
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler {
  private notification = inject(NotificationService);
  private zone = inject(NgZone);

  /** Throttle: maximo 1 notificacion de error cada 2 segundos */
  private lastErrorTime = 0;
  private errorCount = 0;
  private readonly ERROR_THROTTLE_MS = 2000;
  private readonly MAX_ERRORS_PER_WINDOW = 3;

  handleError(error: Error | HttpErrorResponse): void {
    const now = Date.now();

    // Reset contador si paso el intervalo de throttle
    if (now - this.lastErrorTime > this.ERROR_THROTTLE_MS) {
      this.errorCount = 0;
    }

    this.errorCount++;
    this.lastErrorTime = now;

    // Reportar a Sentry vía createErrorHandler (errores no-HTTP o HTTP 5xx)
    if (sentryHandler && this.shouldReportToSentry(error)) {
      sentryHandler.handleError(error);
    }

    // Throttle: si hay demasiados errores en un periodo corto, solo loguear
    if (this.errorCount > this.MAX_ERRORS_PER_WINDOW) {
      console.error('Error suprimido (throttle):', error);
      return;
    }

    // Ejecutar en la zona de Angular para que las notificaciones se muestren correctamente
    this.zone.run(() => {
      if (error instanceof HttpErrorResponse) {
        this.handleHttpError(error);
      } else {
        this.handleClientError(error);
      }
    });

    // Log del error para debugging
    console.error('Error capturado por GlobalErrorHandler:', error);
  }

  private handleHttpError(error: HttpErrorResponse): void {
    let message: string;

    if (!navigator.onLine) {
      message = 'Sin conexión a internet. Verifica tu conexión.';
    } else {
      message = this.getHttpErrorMessage(error);
    }

    this.notification.showError(message);
  }

  private getHttpErrorMessage(error: HttpErrorResponse): string {
    switch (error.status) {
      case 0:
        return 'No se pudo conectar con el servidor. Intenta más tarde.';
      case 400:
        return this.extractErrorMessage(error) || 'Solicitud inválida.';
      case 401:
        return 'Sesión expirada. Por favor, inicia sesión nuevamente.';
      case 403:
        return 'No tienes permisos para realizar esta acción.';
      case 404:
        return 'Recurso no encontrado.';
      case 409:
        return this.extractErrorMessage(error) || 'Conflicto con el estado actual del recurso.';
      case 422:
        return this.extractErrorMessage(error) || 'Datos de entrada inválidos.';
      case 429:
        return 'Demasiadas solicitudes. Espera un momento.';
      case 500:
        return 'Error interno del servidor. Intenta más tarde.';
      case 502:
      case 503:
      case 504:
        return 'Servicio temporalmente no disponible. Intenta más tarde.';
      default:
        return this.extractErrorMessage(error) || `Error del servidor (${error.status})`;
    }
  }

  private extractErrorMessage(error: HttpErrorResponse): string | null {
    const body = error.error as ApiError;

    if (typeof body === 'string') {
      return body;
    }

    if (body?.message) {
      return body.message;
    }

    if (body?.error) {
      return body.error;
    }

    if (body?.errors && body.errors.length > 0) {
      return body.errors.join('. ');
    }

    return null;
  }

  private handleClientError(error: Error): void {
    // Ignorar errores de navegación cancelada
    if (error.message?.includes('Navigation ID')) {
      return;
    }

    // Ignorar errores de ExpressionChangedAfterItHasBeenCheckedError en desarrollo
    if (error.message?.includes('ExpressionChangedAfterItHasBeenCheckedError')) {
      console.warn('ExpressionChangedAfterItHasBeenCheckedError detectado:', error.message);
      return;
    }

    // Ignorar errores de Google Sign-In cuando no está configurado
    if (error.message?.includes('google') || error.message?.includes('gsi')) {
      console.warn('Google Sign-In error (ignorado):', error.message);
      return;
    }

    // Errores de chunk loading (lazy loading fallido)
    if (error.message?.includes('Loading chunk') || error.message?.includes('ChunkLoadError')) {
      this.notification.showWarning('Error al cargar el módulo. Recargando página...');
      setTimeout(() => window.location.reload(), 2000);
      return;
    }

    // Error genérico para el usuario
    this.notification.showError('Ha ocurrido un error inesperado.');
  }

  private shouldReportToSentry(error: Error | HttpErrorResponse): boolean {
    if (error instanceof HttpErrorResponse) {
      // Solo reportar errores de servidor; 4xx son típicamente del cliente
      return error.status === 0 || error.status >= 500;
    }
    const msg = error?.message ?? '';
    if (msg.includes('Navigation ID')) return false;
    if (msg.includes('ExpressionChangedAfterItHasBeenCheckedError')) return false;
    if (msg.includes('Loading chunk') || msg.includes('ChunkLoadError')) return false;
    if (msg.includes('google') || msg.includes('gsi')) return false;
    return true;
  }
}
